<template>
  <section class="diagnosis-setup mb-4" aria-labelledby="diagnosis-setup-title">
    <div class="diagnosis-setup__header">
      <div>
        <h3 id="diagnosis-setup-title" class="diagnosis-setup__title">Get your diagnosis ready</h3>
        <p class="diagnosis-setup__subtitle">
          Two quick steps help the diagnosis find every mention of your brand and your doctors.
        </p>
      </div>
      <span class="diagnosis-setup__progress" :class="{ 'is-complete': canRun }">
        {{ readyCount }} of {{ rows.length }} ready
      </span>
    </div>

    <div
      v-for="row in rows"
      :key="row.item"
      class="diagnosis-setup__row"
      :class="`is-${row.state}`"
    >
      <v-progress-circular
        v-if="row.state === 'adding'"
        indeterminate
        color="primary"
        size="20"
        width="2"
        class="diagnosis-setup__mark"
        aria-hidden="true"
      />
      <v-icon v-else class="diagnosis-setup__mark" size="22" aria-hidden="true">{{ markIcon(row.state) }}</v-icon>
      <div class="diagnosis-setup__copy">
        <div class="diagnosis-setup__row-title">{{ row.title }}</div>
        <div class="diagnosis-setup__row-helper">{{ row.helperText }}</div>
      </div>
      <div class="diagnosis-setup__actions">
        <template v-if="row.state === 'open'">
          <button
            type="button"
            class="diagnosis-setup__link"
            :disabled="busy"
            @click="$emit('set-dismissal', { item: row.item, dismissed: true })"
          >
            Dismiss
          </button>
          <v-btn
            variant="outlined"
            color="primary"
            rounded="pill"
            class="text-none"
            @click="$emit('go-to-tab', row.tab)"
          >
            {{ row.cta }} →
          </v-btn>
        </template>
        <span v-else-if="row.state === 'done'" class="diagnosis-setup__chip is-done">Done</span>
        <span v-else-if="row.state === 'adding'" class="diagnosis-setup__chip is-adding">Adding…</span>
        <template v-else>
          <span class="diagnosis-setup__chip is-skipped">Skipped</span>
          <button
            type="button"
            class="diagnosis-setup__link"
            :disabled="busy"
            @click="$emit('set-dismissal', { item: row.item, dismissed: false })"
          >
            Undo
          </button>
        </template>
      </div>
    </div>

    <div class="diagnosis-setup__footer">
      <v-btn
        variant="flat"
        rounded="pill"
        class="diagnosis-setup__run text-none"
        :class="{ 'is-ready': canRun }"
        :disabled="!canRun || running"
        @click="$emit('run')"
      >
        <v-progress-circular v-if="running" indeterminate size="14" width="2" class="mr-2" />
        <v-icon v-else start size="16">mdi-lightning-bolt</v-icon>
        {{ running ? 'Starting…' : 'Run Diagnosis' }}
      </v-btn>
      <span v-if="runHint" class="diagnosis-setup__hint">{{ runHint }}</span>
    </div>
    <p v-if="error" class="diagnosis-setup__error" role="alert">{{ error }}</p>
  </section>
</template>

<script>
export default {
  name: 'DiagnosisSetupChecklist',
  props: {
    providerCount: {
      type: Number,
      default: 0
    },
    matchStringCount: {
      type: Number,
      default: 0
    },
    dismissals: {
      type: Object,
      default: () => ({})
    },
    running: {
      type: Boolean,
      default: false
    },
    busy: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    // A provider was just added and its comparison isn't saved yet.
    providersPending: {
      type: Boolean,
      default: false
    }
  },
  emits: ['go-to-tab', 'set-dismissal', 'run'],
  computed: {
    rows() {
      return [
        {
          item: 'providers',
          tab: 'provider_review',
          title: 'Add your providers',
          helper: 'Add the doctors you want the diagnosis to look at.',
          doneHelper: `${this.providerCount} ${this.providerCount === 1 ? 'provider' : 'providers'} added.`,
          cta: 'Add providers',
          count: this.providerCount,
          pending: this.providersPending
        },
        {
          item: 'match_strings',
          tab: 'visibility',
          title: 'Add brand match strings',
          helper: 'Add other names AI uses for your hospital, like a campus or old name.',
          doneHelper: `${this.matchStringCount} ${this.matchStringCount === 1 ? 'match string' : 'match strings'} saved.`,
          cta: 'Add match strings',
          count: this.matchStringCount
        }
      ].map((row) => {
        const state = this.rowState(row)
        return { ...row, state, helperText: this.helperFor(row, state) }
      })
    },
    readyCount() {
      return this.rows.filter((row) => row.state === 'done' || row.state === 'skipped').length
    },
    canRun() {
      return this.readyCount === this.rows.length
    },
    runHint() {
      if (this.providersPending) return 'Run Diagnosis turns on once your new provider has finished adding.'
      if (!this.canRun) return 'Finish or skip both steps to run your diagnosis.'
      return ''
    }
  },
  methods: {
    // An add in progress wins, then data over a dismissal: a skipped step that later
    // gets data shows as done.
    rowState(row) {
      if (row.pending) return 'adding'
      if (row.count > 0) return 'done'
      if (this.dismissals?.[row.item]) return 'skipped'
      return 'open'
    },
    helperFor(row, state) {
      if (state === 'adding') return 'Adding your provider. This takes about a minute.'
      if (state === 'done') return row.doneHelper
      if (state === 'skipped') return 'Skipped for now, you can always add it later'
      return row.helper
    },
    markIcon(state) {
      if (state === 'done') return 'mdi-check-circle'
      if (state === 'skipped') return 'mdi-minus-circle-outline'
      return 'mdi-circle-outline'
    }
  }
}
</script>

<style scoped>
.diagnosis-setup {
  background: #ffffff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(13, 30, 53, 0.06);
  padding: 24px;
}

.diagnosis-setup__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.diagnosis-setup__title {
  font-family: 'Lora', serif;
  font-size: 1.375rem;
  font-weight: 600;
  color: var(--brand-ink-950);
  margin: 0 0 4px;
}

.diagnosis-setup__subtitle {
  margin: 0;
  max-width: 460px;
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(13, 36, 64, 0.62);
}

.diagnosis-setup__progress {
  flex-shrink: 0;
  padding: 4px 12px;
  border-radius: 9999px;
  background: var(--brand-sand-100);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--brand-ink-900);
  white-space: nowrap;
}

.diagnosis-setup__progress.is-complete {
  background: var(--brand-teal-100);
}

.diagnosis-setup__row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 12px;
  border: 1px solid var(--brand-sand-300);
  border-radius: 12px;
  background: #ffffff;
}

.diagnosis-setup__row.is-done {
  background: var(--brand-sand-100);
  border-color: var(--brand-teal-500);
}

.diagnosis-setup__row.is-skipped {
  background: var(--brand-sand-100);
}

.diagnosis-setup__mark {
  flex-shrink: 0;
  color: var(--brand-sand-300) !important;
}

.diagnosis-setup__row.is-done .diagnosis-setup__mark {
  color: var(--brand-teal-500) !important;
}

.diagnosis-setup__row.is-skipped .diagnosis-setup__mark {
  color: rgba(13, 36, 64, 0.45) !important;
}

.diagnosis-setup__copy {
  flex: 1 1 auto;
  min-width: 0;
}

.diagnosis-setup__row-title {
  font-weight: 700;
  color: var(--brand-ink-950);
}

.diagnosis-setup__row.is-skipped .diagnosis-setup__row-title {
  color: rgba(13, 36, 64, 0.62);
}

.diagnosis-setup__row-helper {
  font-size: 0.875rem;
  color: rgba(13, 36, 64, 0.62);
}

.diagnosis-setup__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.diagnosis-setup__link {
  font-size: 0.875rem;
  color: rgba(13, 36, 64, 0.62);
  text-decoration: underline;
}

.diagnosis-setup__row.is-skipped .diagnosis-setup__link {
  color: var(--brand-ocean-600);
  font-weight: 600;
}

.diagnosis-setup__link:disabled {
  opacity: 0.5;
  cursor: default;
}

.diagnosis-setup__chip {
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 600;
}

.diagnosis-setup__chip.is-done {
  background: var(--brand-teal-100);
  color: var(--brand-ink-950);
}

.diagnosis-setup__chip.is-skipped {
  background: #ffffff;
  color: rgba(13, 36, 64, 0.62);
}

.diagnosis-setup__chip.is-adding {
  background: var(--brand-sand-100);
  color: var(--brand-ink-900);
}

.diagnosis-setup__footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 4px;
  padding-top: 16px;
  border-top: 1px solid var(--brand-sand-300);
}

.diagnosis-setup__run.v-btn {
  background-color: var(--brand-sand-100) !important;
  color: rgba(13, 36, 64, 0.62) !important;
  border: 1px solid var(--brand-sand-300);
  opacity: 1;
}

.diagnosis-setup__run.is-ready.v-btn {
  background-color: var(--brand-ocean-600) !important;
  color: #ffffff !important;
  border-color: var(--brand-ocean-600);
}

.diagnosis-setup__run.v-btn--disabled :deep(.v-btn__overlay) {
  opacity: 0;
}

.diagnosis-setup__hint {
  font-size: 0.875rem;
  color: rgba(13, 36, 64, 0.62);
}

.diagnosis-setup__error {
  margin: 12px 0 0;
  font-size: 0.875rem;
  color: rgb(var(--v-theme-error));
}

@media (max-width: 600px) {
  .diagnosis-setup {
    padding: 18px;
  }

  .diagnosis-setup__row {
    flex-wrap: wrap;
  }

  .diagnosis-setup__actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
